import React, { useEffect, useState } from 'react';
import {
  API,
  showError,
  showSuccess,
  timestamp2string
} from '../helpers';

import {
  Button,
  Divider,
  Dropdown,
  Form,
  Header,
  Icon,
  Input,
  Label,
  Modal,
  Pagination,
  Progress,
  Table
} from 'semantic-ui-react';

// 格式化字节大小
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// 格式化时间
const formatDateTime = (dateString) => {
  if (!dateString || dateString === '0001-01-01T00:00:00Z') return '暂无活动';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'N/A';
  }
};

const GitUsersTable = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('repo_count');

  const pageSize = 10;

  const loadUsers = async (startIdx = 0, sort = sortBy) => {
    setLoading(true);
    try {
      const res = await API.get(`/api/admin/git/stats/users`, {
        params: {
          page: Math.floor(startIdx / pageSize) + 1,
          page_size: pageSize,
          sort: sort
        }
      });
      const { success, message, data, total } = res.data;
      if (success) {
        setUsers(data || []);
        setTotal(total);
      } else {
        showError(message);
      }
    } catch (error) {
      // 如果新接口失败，尝试旧接口
      try {
        const res = await API.get(`/api/admin/git/users`, {
          params: {
            page: Math.floor(startIdx / pageSize) + 1,
            page_size: pageSize
          }
        });
        const { success, message, data, total } = res.data;
        if (success) {
          setUsers(data || []);
          setTotal(total);
        } else {
          showError(message);
        }
      } catch {
      showError('加载用户列表失败');
      }
    }
    setLoading(false);
  };

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      await loadUsers((activePage - 1) * pageSize, sortBy);
      setActivePage(activePage);
    })();
  };

  const handleSortChange = (e, { value }) => {
    setSortBy(value);
    loadUsers(0, value);
    setActivePage(1);
  };

  useEffect(() => {
    loadUsers(0);
  }, []);

  // 计算最大存储大小用于进度条
  const maxSize = Math.max(...users.map(u => u.total_size || 0), 1);

  return (
    <>
      <Header as='h3'>
        <Icon name='users' />
        <Header.Content>
        Git用户统计
          <Header.Subheader>查看所有Git用户的详细统计信息</Header.Subheader>
        </Header.Content>
      </Header>

      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
        <span>排序方式：</span>
        <Dropdown
          selection
          compact
          value={sortBy}
          onChange={handleSortChange}
          options={[
            { key: 'repo_count', text: '仓库数量', value: 'repo_count' },
            { key: 'total_size', text: '存储大小', value: 'total_size' },
            { key: 'username', text: '用户名', value: 'username' }
          ]}
        />
        <Button 
          icon='refresh' 
          content='刷新' 
          size='small' 
          onClick={() => loadUsers(0, sortBy)}
          loading={loading}
        />
      </div>

      <Divider />
      
      <Table striped compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={2}>用户名</Table.HeaderCell>
            <Table.HeaderCell width={2}>仓库统计</Table.HeaderCell>
            <Table.HeaderCell width={3}>存储使用</Table.HeaderCell>
            <Table.HeaderCell width={2}>最后活动</Table.HeaderCell>
            <Table.HeaderCell width={3}>关联用户</Table.HeaderCell>
            <Table.HeaderCell width={2}>角色</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {users.map((user, idx) => {
            const sizePercent = maxSize > 0 ? (user.total_size / maxSize) * 100 : 0;
              return (
              <Table.Row key={user.user_id || user.username}>
                  <Table.Cell>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon name='user' color='blue' />
                    <strong>{user.username}</strong>
                  </div>
                  </Table.Cell>
                  <Table.Cell>
                  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                    <Label size='small' color='blue'>
                      <Icon name='folder' />
                      {user.repo_count || 0} 仓库
                    </Label>
                    {user.public_count !== undefined && (
                      <>
                        <Label size='mini' color='green'>{user.public_count || 0} 公开</Label>
                        <Label size='mini' color='red'>{user.private_count || 0} 私有</Label>
                      </>
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <div style={{ minWidth: '120px' }}>
                    <div style={{ marginBottom: '5px', fontWeight: 'bold' }}>
                      {formatBytes(user.total_size || 0)}
                    </div>
                    <Progress 
                      percent={sizePercent} 
                      size='tiny' 
                      color='blue'
                      style={{ margin: 0 }}
                    />
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    {formatDateTime(user.last_activity)}
                  </span>
                  </Table.Cell>
                  <Table.Cell>
                    {user.one_api_user ? (
                      <div>
                      <div style={{ fontWeight: 'bold' }}>{user.one_api_user.display_name}</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>
                          {user.one_api_user.email}
                        </div>
                      </div>
                    ) : (
                    <Label size='small' color='grey'>未关联</Label>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    {user.is_admin && (
                      <Label size='mini' color='red'>
                        <Icon name='shield' /> Git管理员
                      </Label>
                    )}
                    {user.one_api_user && (
                      <Label 
                        size='mini' 
                        color={user.one_api_user.role >= 10 ? 'orange' : 'blue'}
                      >
                        {user.one_api_user.role >= 10 ? 'API管理员' : 'API用户'}
                      </Label>
                    )}
                    {!user.is_admin && !user.one_api_user && (
                      <Label size='mini' color='grey'>普通用户</Label>
                    )}
                  </div>
                  </Table.Cell>
                </Table.Row>
              );
            })}
        </Table.Body>

        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan='6'>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ color: '#666' }}>
                  <Icon name='users' />
                  总计: <strong>{total}</strong> 个Git用户
                </div>
                <Pagination
                  activePage={activePage}
                  onPageChange={onPaginationChange}
                  size='small'
                  siblingRange={1}
                  totalPages={Math.ceil(total / pageSize)}
                />
              </div>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
    </>
  );
};

export default GitUsersTable; 